/**
 * MenuWeekView component
 * Displays the full weekly menu in a grid layout
 */

import { useTranslation } from 'react-i18next';
import { MenuDayCard } from './MenuDayCard';
import type { WeekDay } from '@/types/api';

interface MenuWeekViewProps {
  weekData: WeekDay[];
  isLoading?: boolean;
}

/**
 * MenuWeekView component
 *
 * Features:
 * - Display 7-day menu grid
 * - Highlight today
 * - Loading skeleton
 * - Empty state if no menu set
 * - Responsive layout
 *
 * @example
 * ```tsx
 * <MenuWeekView weekData={menuData} isLoading={isLoading} />
 * ```
 */
export function MenuWeekView({ weekData, isLoading }: MenuWeekViewProps) {
  const { t } = useTranslation();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Use local date instead of UTC to avoid timezone issues
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-md p-4 h-48 animate-pulse"
          >
            <div className="h-6 bg-gray-200 rounded w-1/2 mx-auto mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (weekData.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-md">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('menu.noMenuAvailable')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('menu.menuNotSet')}
        </p>
      </div>
    );
  }

  // Week menu grid
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-4">
      {weekData.map((dayData) => {
        const isToday = dayData.date === todayString;
        const isPast = dayData.date < todayString;
        return (
          <MenuDayCard
            key={dayData.date}
            dayData={dayData}
            isToday={isToday}
            isPast={isPast}
          />
        );
      })}
    </div>
  );
}
